import { Field, useFormState } from 'react-final-form'
import moment from 'moment'

import Badge from 'common/components/Badge'
import ContentTypeBubble from 'common/components/ContentTypeBubble'
import DatePicker from 'common/components/datePicker/DatePicker'
import { TextareaType } from 'common/components/dynamicFields/interfaces'
import FormItem from 'common/components/FormItem'
import Input from 'common/components/Input'
import SingleSelect, { SelectValue } from 'common/components/singleSelect'
import Spinner from 'common/components/spinner'
import useProjectsQuery from 'features/campaigns/queries/useProjectsQuery'
import useGetSplitCalculatorConfigurationQuery from 'features/unifiedFlow/api/queries/useGetSplitCalculatorConfigurationQuery'

import { useExperimentForm } from '../context/ExperimentFormContext'
import { getExperimentPrefixedName, getSelectedProjectId } from '../helpers'
import SplitCalculator from '../splitCalculator'

import ControlSubjectLine from './ControlSubjectLine'

import './DatePickerStyles.css'

const { REACT_APP_DEFAULT_DATE_FORMAT } = process.env

const validateProject = (value: SelectValue) => {
  if (!value) {
    return 'Select your project.'
  }
}
const validateName = (value: string | undefined) => {
  if (!value?.trim()) {
    return 'Enter a name for your experiment.'
  }
}
const validateDate = (value: string | undefined) => {
  if (!value) {
    return 'Select a date.'
  }
}

const SetUpExperiment = () => {
  const {
    state: { template },
  } = useExperimentForm()

  const formState = useFormState()

  const selectedProjectId =
    template && getSelectedProjectId(formState, template)

  const { data: projects } = useProjectsQuery()
  const projectOptions = projects
    ?.filter(
      (project) =>
        project.project_configuration?.campaign_configurations?.testing_method
          ?.tested_content_sections?.[0]?.section ===
          template?.optimization?.contentType && project.deleted !== true
    )
    ?.map((project) => ({
      label: project.name,
      value: project._id,
    }))
    .sort((a, b) => {
      return a.label.toLowerCase().localeCompare(b.label.toLowerCase())
    })

  const singleProject =
    projectOptions && projectOptions.length === 1
      ? projectOptions[0]
      : undefined

  const { data: projectSplitCalculatorConfiguration, isLoading } =
    useGetSplitCalculatorConfigurationQuery({
      projectId: selectedProjectId,
    })

  const controlSubjectLineConfig =
    projectSplitCalculatorConfiguration?.experimentMainFields?.find(
      (field) => field.name === 'own_subject_line'
    )
  const dateConfig =
    projectSplitCalculatorConfiguration?.experimentMainFields?.find(
      (field) => field.name === 'iso_send_date'
    )

  const splitCalculatorConfiguration =
    projectSplitCalculatorConfiguration?.splitCalculation
  const integrationType = projectSplitCalculatorConfiguration?.integrationType

  if (!template) {
    return null
  }

  const getState = () => {
    if (!selectedProjectId) {
      return 'noData'
    } else if (isLoading && selectedProjectId) {
      return 'loading'
    } else {
      return 'data'
    }
  }

  const state = getState()

  return (
    <div className="SetUpExperiment flex flex-col w-full">
      <div className="flex items-center mb-4">
        <ContentTypeBubble
          name={template?.displayName}
          iconType={template?.icon || ''}
        />
        <div className="text-base font-medium flex ml-4 text-coolGray-800">
          {template?.displayName}
        </div>
        <Badge
          text="Optimize"
          variant="optimize"
          size="small"
          className="capitalize ml-auto"
        />
      </div>
      <Field<string>
        name={getExperimentPrefixedName(template, 'experimentName')}
        validate={validateName}
      >
        {({ input, meta }) => (
          <FormItem
            error={meta.touched ? meta.error : undefined}
            htmlFor={input.name}
            label="Experiment name"
            className="flex-none mb-4"
          >
            <Input
              data-cy="experimentName-input"
              type="text"
              variant="default"
              className="w-full focus:bg-orange-50"
              value={input.value}
              onChange={input.onChange}
              onBlur={input.onBlur}
              name={input.name}
              id={input.name}
              placeholder="Give your experiment a memorable name"
            />
          </FormItem>
        )}
      </Field>
      <div className="flex justify-between gap-4">
        <Field<SelectValue>
          name={getExperimentPrefixedName(template, 'project')}
          validate={validateProject}
        >
          {({ input, meta }) => (
            <FormItem
              label="Project"
              htmlFor={input.name}
              error={meta.touched ? meta.error : undefined}
              className="w-full"
            >
              <SingleSelect
                isSearchable
                className="w-full"
                data-cy="project-select"
                id={input.name}
                name={input.name}
                placeholder="Select your project"
                value={input.value.value || singleProject?.value}
                onChange={input.onChange}
                onBlur={input.onBlur}
                options={projectOptions}
                menuPortalTarget={document.body}
                tabIndex={-1} // TODO: Default tabIndex={0} is causing an issue with the slidePanel when navigating with TAB key
              />
            </FormItem>
          )}
        </Field>
        {dateConfig && (
          <Field<string>
            name={getExperimentPrefixedName(template, 'sendDate')}
            validate={dateConfig.required ? validateDate : undefined}
          >
            {({ input, meta }) => (
              <FormItem
                label={dateConfig.label}
                htmlFor={input.name}
                error={meta.touched ? meta.error : undefined}
                className="w-full"
              >
                <DatePicker
                  onDateChange={(date) =>
                    input.onChange(
                      date ? date.format(REACT_APP_DEFAULT_DATE_FORMAT) : null
                    )
                  }
                  date={input.value ? moment(input.value) : null}
                  id={input.name}
                  isOutsideRange={() => false}
                  numberOfMonths={1}
                  onBlur={input.onBlur}
                />
              </FormItem>
            )}
          </Field>
        )}
      </div>
      {
        {
          noData: null,
          loading: <Spinner />,
          data: (
            <>
              {controlSubjectLineConfig && (
                <ControlSubjectLine
                  template={template}
                  config={controlSubjectLineConfig as TextareaType}
                  integrationType={integrationType}
                />
              )}
              {splitCalculatorConfiguration && (
                <SplitCalculator
                  template={template}
                  configuration={splitCalculatorConfiguration}
                />
              )}
            </>
          ),
        }[state]
      }
    </div>
  )
}

export default SetUpExperiment
